import styled, { css } from 'styled-components'
import { Box, Link } from '../components'

const EditIconWrapper = styled.div`
  opacity: 0.5;
  color: ${p => p.theme.colors.primary};
  transition: 0.3s;
  cursor: pointer;
  padding: 0 0.4rem 0 1.2rem;

  &:hover {
    opacity: 1;
  }
`

const DeleteIconWrapper = styled.div`
  opacity: 0.5;
  color: ${p => p.theme.colors.red};
  transition: 0.3s;
  cursor: pointer;
  padding: 0 0.4rem;

  &:hover {
    opacity: 1;
  }
`

export const CartBox = styled(Box)``

export const CartHeader = styled(Box.Header)`
  padding: 0;
  align-items: stretch;
  border-bottom: none;
`

export const CartContent = styled(Box.Content)`
  padding: 1.5rem 5rem 3rem;
`

export const CartMetaFields = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 1.6rem 2.4rem;
  margin-bottom: 2rem;
  padding-bottom: 1.6rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.groupBorder};
`

export const CartMetaFieldsGroup = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 1.6rem 2.4rem;
  flex: 0 0 auto;
`

export const CartMetaCylinderSign = styled.div`
  flex: 0 0 auto;
  margin-left: auto;
  margin-right: auto;
`

export const CartMetaActions = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 2.4rem;
  flex: 0 0 auto;
`

export const CartMetaField = styled.div`
  flex: 0 0 36rem;
  width: 36rem;
`

export const EmptyState = styled.p`
  margin: 2rem 0;
  color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`

export const CartGroupList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
`

export const CartProductGroup = styled.div`
  ${({ theme }) => css`
    border: 1px solid ${theme.colors.groupBorder};
    border-radius: ${theme.field?.borderRadius || '0.4rem'};
  `}
`

export const CartProductHeader = styled.div`
  ${({ $hasStickyHeader, $isExpanded, theme }) => {
    const borderRadius = theme.field?.borderRadius || '0.4rem'

    return css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1.2rem;
      position: ${$hasStickyHeader ? 'sticky' : 'static'};
      top: ${$hasStickyHeader ? '0' : 'auto'};
      z-index: ${$hasStickyHeader ? 2 : 'auto'};
      padding: 1rem 1.2rem;
      background-color: ${theme.colors.tableOddRowBackground || '#f9f9f9'};
      border-bottom: ${$isExpanded ? `1px solid ${theme.colors.groupBorder}` : 'none'};
      transform: ${$hasStickyHeader ? 'translateZ(0)' : 'none'};
      border-radius: ${$isExpanded
        ? `${borderRadius} ${borderRadius} 0 0`
        : borderRadius};
      transition: background-color 0.2s ease;

      &:hover {
        background-color: ${theme.colors.pageBackground || theme.colors.gray || '#f6f6f6'};
      }
    `
  }}
`

export const CartProductHeaderToggle = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    flex: 1;
    min-width: 0;
    padding: 0.4rem 0.6rem;
    margin: -0.4rem -0.6rem;
    border: none;
    border-radius: ${theme.field?.borderRadius || '0.4rem'};
    background: transparent;
    cursor: pointer;
    text-align: left;

    &:focus-visible {
      outline: 2px solid ${theme.colors.primary};
      outline-offset: 1px;
    }
  `}
`

export const CartProductChevron = styled.span`
  ${({ $isExpanded, theme }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: transform 0.15s ease;
    transform: rotate(${$isExpanded ? '180deg' : '0deg'});
    color: ${theme.colors.text || theme.colors.text2 || '#333'};
  `}
`

export const CartProductInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.8rem;
  min-width: 0;
`

export const CartProductName = styled.span`
  font-size: 1.4rem;
  font-weight: 600;
`

export const CartProductMeta = styled.span`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.textSecondary || '#666'};
  white-space: nowrap;
`

export const CartLinesTable = styled.table`
  width: 100%;
  margin-top: ${({ $hasStickyHeader }) => $hasStickyHeader ? '-1px' : '0'};
  border-collapse: separate;
  border-spacing: 0;
  font-size: 1.4rem;
`

export const CartLinesHead = styled.thead`
  ${({ $hasStickyHeader, $readOnly, theme }) => css`
    th {
      position: ${$hasStickyHeader ? 'sticky' : 'static'};
      top: ${$hasStickyHeader
        ? 'calc(var(--cart-product-header-height, 4.8rem) - 1px)'
        : 'auto'};
      z-index: ${$hasStickyHeader ? 1 : 'auto'};
      padding: 0.8rem;
      text-align: left;
      font-weight: 600;
      white-space: nowrap;
      font-size: 1.3rem;
      background-color: ${theme.colors.tableOddRowBackground || '#f9f9f9'};
      border-bottom: 1px solid ${theme.colors.groupBorder};
      transform: ${$hasStickyHeader ? 'translateZ(0)' : 'none'};
    }

    th:last-child {
      width: ${$readOnly ? 'auto' : '1%'};
      text-align: ${$readOnly ? 'left' : 'right'};
      padding-right: 1.2rem;
    }
  `}
`

export const CartLinesBody = styled.tbody`
  tr:nth-child(odd) {
    background-color: ${({ theme }) => theme.colors.tableOddRowBackground || '#f9f9f9'};
  }

  td {
    padding: 0.8rem;
    vertical-align: middle;
  }

  td:last-child {
    width: ${({ $readOnly }) => ($readOnly ? 'auto' : '1%')};
    text-align: ${({ $readOnly }) => ($readOnly ? 'left' : 'right')};
    padding-right: 1.2rem;
  }
`

export const QtyInput = styled.input`
  ${({ theme }) => css`
    width: 5rem;
    padding: 0.4rem 0.6rem;
    border: 1px solid ${theme.colors.groupBorder};
    border-radius: ${theme.field?.borderRadius || '0.4rem'};
    font-size: 1.4rem;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `}
`

export const CartProductActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-shrink: 0;
`

export const EditProductLink = styled(EditIconWrapper).attrs({
  as: Link,
})`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  text-decoration: none;
  font-size: 1.3rem;
  white-space: nowrap;
  padding: 0 0.4rem;
`

export const RemoveProductButton = styled(DeleteIconWrapper).attrs({
  as: 'button',
  type: 'button',
})`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  border: none;
  background: transparent;
  font-size: 1.3rem;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const RemoveLineButton = styled(DeleteIconWrapper).attrs({
  as: 'button',
  type: 'button',
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const CartSummary = styled.div`
  ${({ theme }) => css`
    margin: 2rem 0 0;
    padding-top: 1.2rem;
    border-top: 1px solid ${theme.colors.groupBorder};
    font-size: 1.4rem;
    color: ${theme.colors.textSecondary || '#666'};
  `}
`

export const CartSummaryText = styled.span`
  display: block;
`

export const RemoveProductConfirmProductLine = styled.div``

export const RemoveProductConfirmMessage = styled.div`
  margin-top: 0.8rem;
`
